import { useEffect, useState } from "react";
import { Box, Button, Text } from "@salestrak/ui";

import type { Workspace } from "#app/lib/data/workspace-context";
import { useAuthEmail } from "#app/lib/hooks/use-auth-email";
import { pluralize } from "#app/lib/utils/pluralize";
import { WorkspaceAvatar } from "#app/components/brand/workspace-avatar";
import { WorkspaceScreenLayout } from "#app/components/workspace-screen-layout";
import { AnalyticsContext, WorkspaceEvent, trackEvent } from "#app/lib/analytics";

/**
 * The one screen a pending invite is decided on. It stands where the app
 * would be while the signed-in user holds an `invited` row: the app shell
 * (`routes/(app)/route.tsx`) renders it ahead of everything else on load, one
 * invite at a time, so an invite never needs a nudge inside a workspace and
 * never has to be gone looking for.
 *
 * Deliberately modal: Join and Decline are the only two ways through, and
 * either clears the gate. There is no "not now" — a dismissed invite would
 * sink into the picker where nobody looks, which is the reminder problem this
 * screen exists to remove. The account chip (the layout's "Logged in as"
 * menu) is the one exit that is not a decision: an invite is bound to an
 * email, so someone looking at the wrong account can log out without
 * declining on that account's behalf. The gate fires again when the invited
 * user signs in.
 *
 * Same chrome as the picker (WorkspaceScreenLayout), so the two read as one
 * family — the picker's invited row is the same decision, in list form.
 */
export function WorkspaceInviteScreen({
  workspace,
  onJoin,
  onDecline,
  via,
}: {
  workspace: Workspace;
  /** The door, for `workspace.invite_gate_shown`: "gate" or "email_link". */
  via: string;
  onJoin: () => Promise<void>;
  onDecline: () => Promise<void>;
}) {
  const email = useAuthEmail();
  // Once per invite shown: the denominator of the accept rate.
  const workspaceId = workspace.workspaceId;
  useEffect(() => {
    trackEvent({
      context: AnalyticsContext.Workspace,
      event: WorkspaceEvent.InviteGateShown,
      data: { via },
    });
  }, [workspaceId, via]);
  // One decision in flight at a time; the other button rests while it runs.
  const [busy, setBusy] = useState<"join" | "decline" | null>(null);

  const run = (action: "join" | "decline") => async () => {
    if (busy) return;
    setBusy(action);
    try {
      await (action === "join" ? onJoin() : onDecline());
    } finally {
      setBusy(null);
    }
  };

  const inviter = workspace.viewerInvitedBy?.name;
  const members = `${workspace.memberCount} ${pluralize("member", {
    count: workspace.memberCount,
  })}`;

  return (
    <WorkspaceScreenLayout email={email}>
      <Box direction="column" align="center">
        <Box direction="column" align="center" fill="width" maxWidth={360}>
          <WorkspaceAvatar
            name={workspace.name}
            logo={workspace.settings?.logo}
            avatarColor={workspace.avatarColor}
            size="lg"
          />
          <Box paddingTop="lg" paddingBottom="sm">
            <Text as="h1" size="2xl" weight="medium" align="center">
              Join {workspace.name}
            </Text>
          </Box>
          <Text variant="muted" align="center">
            {inviter ? `${inviter} invited you` : "You've been invited"} · {members}
          </Text>
          <Box fill="width" paddingTop="xl" gap="sm" align="center">
            <Button
              size="xl"
              shape="square"
              fullWidth
              onClick={run("join")}
              loading={busy === "join"}
              disabled={busy === "decline"}
            >
              Join workspace
            </Button>
            <Button
              variant="ghost"
              shape="square"
              onClick={run("decline")}
              loading={busy === "decline"}
              disabled={busy === "join"}
            >
              Decline invite
            </Button>
          </Box>
        </Box>
      </Box>
    </WorkspaceScreenLayout>
  );
}
